/**
 * Theme JS - Core Interactivity for Custom Shopify Theme
 */

document.addEventListener('DOMContentLoaded', () => {
  initMobileMenu();
  initHeaderSelectors();
  initCartDrawer();
  initCartAjaxHandlers();
  initProductPageBuyButtons();
  initSearchModal();
  initBackToTop();
  initMobileFooterAccordions();
  initProductCards();
  initCollectionFilters();
  initMainProductWishlist();
  initWishlistDrawer();
  initCartCoupon();

  if (window.themeWishlist) {
    window.themeWishlist.updateUI();
  }

  // Track Collection Page Context for Breadcrumbs
  if (window.location.pathname.startsWith('/collections/') && !window.location.pathname.includes('/products/')) {
    const titleEl = document.querySelector('.collection-main-title') || document.querySelector('h1');
    if (titleEl) {
      sessionStorage.setItem('last_collection_url', window.location.pathname + window.location.search);
      sessionStorage.setItem('last_collection_title', titleEl.textContent.trim());
    }
  }
});

/**
 * Initializes the mobile menu drawer and teleportation
 */
function initMobileMenu() {
  const drawer = document.getElementById('MobileNavDrawer');
  const overlay = document.querySelector('.mobile-drawer-overlay');

  if (!drawer) return;

  // Clean up any existing duplicate drawer elements in body
  const existingDrawers = document.querySelectorAll('body > .mobile-navigation-drawer');
  existingDrawers.forEach(d => {
    if (d !== drawer) d.remove();
  });

  const existingOverlays = document.querySelectorAll('body > .mobile-drawer-overlay');
  existingOverlays.forEach(o => {
    if (o !== overlay) o.remove();
  });

  // Teleport drawer and overlay to body to escape the header's CSS transform containing block
  if (overlay && overlay.parentNode !== document.body) {
    document.body.appendChild(overlay);
  }
  if (drawer && drawer.parentNode !== document.body) {
    document.body.appendChild(drawer);
  }
}

// Helper to close mobile menu
const closeMobileMenu = () => {
  const drawer = document.getElementById('MobileNavDrawer');
  const overlay = document.querySelector('.mobile-drawer-overlay');
  if (drawer) {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    const hamburger = document.querySelector('.mobile-menu-toggle');
    if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
  }
  if (overlay) overlay.classList.remove('is-active');
  document.body.style.overflow = '';
};

// Global delegated event listeners for mobile navigation drawer, cart drawer, and wishlist drawer
document.addEventListener('click', (e) => {
  // 1. Toggle Open Mobile Nav
  const toggleBtn = e.target.closest('.mobile-menu-toggle');
  if (toggleBtn) {
    e.preventDefault();
    const drawer = document.getElementById('MobileNavDrawer');
    const overlay = document.querySelector('.mobile-drawer-overlay');
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      toggleBtn.setAttribute('aria-expanded', 'true');
    }
    if (overlay) overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    return;
  }

  // 2. Toggle Close Mobile Nav
  const closeBtn = e.target.closest('.mobile-drawer-close');
  const overlayClick = e.target.matches('.mobile-drawer-overlay');
  if (closeBtn || overlayClick) {
    e.preventDefault();
    closeMobileMenu();
    return;
  }

  // 3. Open Wishlist Drawer
  const wishlistTrigger = e.target.closest('#HeaderWishlistTrigger, #MobileWishlistTrigger, .hamburger-wishlist-trigger');
  if (wishlistTrigger) {
    e.preventDefault();
    const wishlistDrawer = document.getElementById('WishlistDrawer');
    if (wishlistDrawer) {
      closeMobileMenu();
      wishlistDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }
    return;
  }

  // 4. Open Cart Drawer
  const cartTrigger = e.target.closest('.cart-badge-container, .cart-drawer-trigger');
  if (cartTrigger) {
    e.preventDefault();
    const cartDrawer = document.getElementById('CartDrawer');
    const recPanel = document.getElementById('CartDrawerRecs');
    if (cartDrawer) {
      closeMobileMenu();
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (recPanel) recPanel.classList.remove('is-closed');
    }
    return;
  }

  // 5. Submenu Toggle Accordion
  const submenuToggle = e.target.closest('.mobile-submenu-toggle-btn');
  if (submenuToggle) {
    e.preventDefault();
    const parent = submenuToggle.closest('.mobile-drawer-menu-item');
    const list = parent ? parent.querySelector('.mobile-drawer-submenu-list') : null;
    const plusIcon = submenuToggle.querySelector('.toggle-icon-plus');

    if (list) {
      const isOpen = list.classList.contains('is-open');
      if (isOpen) {
        list.classList.remove('is-open');
        if (plusIcon) plusIcon.textContent = '+';
      } else {
        list.classList.add('is-open');
        if (plusIcon) plusIcon.textContent = '−';
      }
      submenuToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    return;
  }

  // 6. Grandchild Submenu Toggle Accordion
  const grandchildToggle = e.target.closest('.mobile-grandchild-toggle-btn');
  if (grandchildToggle) {
    e.preventDefault();
    const parent = grandchildToggle.closest('.mobile-drawer-submenu-item');
    const list = parent ? parent.querySelector('.mobile-drawer-grandchild-list') : null;
    const chevron = grandchildToggle.querySelector('.toggle-chevron');

    if (list) {
      const isOpen = list.classList.contains('is-open');
      if (isOpen) {
        list.classList.remove('is-open');
        if (chevron) chevron.classList.remove('is-open');
      } else {
        list.classList.add('is-open');
        if (chevron) chevron.classList.add('is-open');
      }
      grandchildToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    }
    return;
  }
});

// Close drawer on Escape key
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') {
    closeMobileMenu();
  }
});

// Shopify Theme Editor section load listener to re-teleport header drawer elements
document.addEventListener('shopify:section:load', (e) => {
  if (e.detail.sectionId === 'header') {
    initMobileMenu();
  }
});

/**
 * Initializes the Language and Currency selector interactions
 */
function initHeaderSelectors() {
  const dropdownTriggers = document.querySelectorAll('.header-dropdown-trigger');

  dropdownTriggers.forEach(trigger => {
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      const name = trigger.textContent.trim().split(' ')[0];
      // For now, showcase a premium native browser feedback or custom modal.
      // In Shopify production, these are connected to currency/language forms.
      console.log(`Dropdown triggered for: ${name}`);
    });
  });
}

/**
 * Initializes the interactive Cart Drawer (Double-Panel layout)
 */
function initCartDrawer() {
  const cartDrawer = document.getElementById('CartDrawer');
  const cartClose = document.querySelector('.cart-drawer-close');
  const cartOverlay = document.querySelector('.cart-drawer-overlay');
  const continueShopping = document.querySelector('.continue-shopping-link');

  // Recommendations slide-out trigger
  const recClose = document.querySelector('.recommendations-close');
  const recPanel = document.getElementById('CartDrawerRecs');

  if (!cartDrawer) return;

  // Open Cart Drawer
  const cartTriggers = document.querySelectorAll('.cart-badge-container, .cart-drawer-trigger');
  cartTriggers.forEach(trigger => {
    if (trigger.dataset.drawerInitialized) return;
    trigger.dataset.drawerInitialized = 'true';
    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      cartDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden'; // Lock background scrolling
      // Ensure the recommendations panel is reset to visible when re-opening
      if (recPanel) recPanel.classList.remove('is-closed');
    });
  });

  // Close Cart Drawer
  const closeDrawer = () => {
    cartDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  if (cartClose) cartClose.addEventListener('click', closeDrawer);
  if (cartOverlay) cartOverlay.addEventListener('click', closeDrawer);
  if (continueShopping) {
    continueShopping.addEventListener('click', (e) => {
      e.preventDefault();
      closeDrawer();
    });
  }

  // Close recommendations panel independently
  if (recClose && recPanel) {
    recClose.addEventListener('click', (e) => {
      e.preventDefault();
      recPanel.classList.add('is-closed');
    });
  }

  // Utility tabs toggle (Note, Coupon, Gift Wrap)
  const tabButtons = cartDrawer.querySelectorAll('.cart-drawer-tab-btn');
  const addonPanels = cartDrawer.querySelectorAll('.cart-drawer-addon-panel');

  tabButtons.forEach(btn => {
    btn.addEventListener('click', function(e) {
      e.preventDefault();
      const tabId = this.getAttribute('data-tab');
      const panel = cartDrawer.querySelector('#AddonPanel-' + tabId);
      if (!panel) return;

      const isAlreadyActive = this.classList.contains('is-active');

      tabButtons.forEach(b => b.classList.remove('is-active'));
      addonPanels.forEach(p => p.classList.remove('is-active'));

      if (!isAlreadyActive) {
        this.classList.add('is-active');
        panel.classList.add('is-active');
      }
    });
  });
}

/**
 * Initializes the premium Centered Search Modal
 */
/**
 * Initializes the premium Centered Search Modal or Search Drawer based on settings and device
 */
function initSearchModal() {
  const triggers = document.querySelectorAll('.search-modal-trigger');
  const modal = document.getElementById('SearchModal');
  const modalOverlay = document.querySelector('.search-modal-overlay');
  const modalCloseBtn = document.querySelector('.search-modal-close');
  const modalInput = document.querySelector('.search-modal-input');

  const drawer = document.getElementById('SearchDrawer');
  const drawerOverlay = document.querySelector('.search-drawer-overlay');
  const drawerCloseBtn = document.querySelector('.search-drawer-close');
  const drawerInput = document.querySelector('.search-drawer-input');

  if (!drawer) return;

  // Teleport overlay y drawer al body para escapar del CSS transform del header
  if (modalOverlay) document.body.appendChild(modalOverlay);
  // El modal va DENTRO del overlay — así el overlay fixed es el scroll container
  if (modal && modalOverlay) modalOverlay.appendChild(modal);
  if (drawerOverlay) document.body.appendChild(drawerOverlay);
  document.body.appendChild(drawer);

  const desktopSearchType = drawer.getAttribute('data-desktop-search-type') || 'popup';
  const isMobile = () => window.innerWidth <= 1024;

  // Open handler
  const openSearch = (e) => {
    if (e) e.preventDefault();
    closeSearch(); // Close any currently open search container first

    if (isMobile() || desktopSearchType === 'drawer') {
      // Open Search Drawer (panel lateral) — bloquea body scroll
      drawer.classList.add('is-open');
      if (drawerOverlay) drawerOverlay.classList.add('is-active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        if (drawerInput) drawerInput.focus();
      }, 150);
    } else {
      // Open Search Modal Popup — el overlay fixed cubre la pantalla y hace scroll
      if (modal) {
        modal.classList.add('is-open');
        if (modalOverlay) {
          modalOverlay.classList.add('is-active');
          modalOverlay.scrollTop = 0; // Siempre empieza desde arriba
        }
        document.body.style.overflow = 'hidden'; // Bloquea el body detrás
        setTimeout(() => {
          if (modalInput) modalInput.focus();
        }, 150);
      }
    }
  };

  // Close handler
  const closeSearch = () => {
    if (modal) modal.classList.remove('is-open');
    if (modalOverlay) modalOverlay.classList.remove('is-active');
    if (drawer) drawer.classList.remove('is-open');
    if (drawerOverlay) drawerOverlay.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  // Bind trigger click events
  triggers.forEach(trigger => {
    trigger.addEventListener('click', openSearch);
  });

  // Bind hamburger menu search trigger, if any exists in mobile nav
  const hamburguerSearchTrigger = document.querySelector('.hamburger-search-trigger');
  if (hamburguerSearchTrigger) {
    hamburguerSearchTrigger.addEventListener('click', openSearch);
  }

  // Close events
  if (modalCloseBtn) modalCloseBtn.addEventListener('click', closeSearch);
  if (modalOverlay) {
    modalOverlay.addEventListener('click', (e) => {
      if (e.target === modalOverlay) {
        closeSearch();
      }
    });
  }
  if (drawerCloseBtn) drawerCloseBtn.addEventListener('click', closeSearch);
  if (drawer) {
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) {
        closeSearch();
      }
    });
  }
  if (drawerOverlay) {
    drawerOverlay.addEventListener('click', (e) => {
      if (e.target === drawerOverlay) {
        closeSearch();
      }
    });
  }

  // Close on Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeSearch();
  });

  // Predictive search logic for Drawer Input
  let debounceTimeout;
  if (drawerInput) {
    const resultsContainer = document.getElementById('SearchDrawerResults');
    const defaultContent = drawer.querySelector('.search-drawer-default-content');
    const loadingSpinner = drawer.querySelector('.search-drawer-loading');

    drawerInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      clearTimeout(debounceTimeout);

      if (query.length < 2) {
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
          resultsContainer.style.display = 'none';
        }
        if (defaultContent) defaultContent.style.display = 'flex';
        if (loadingSpinner) loadingSpinner.style.display = 'none';
        return;
      }

      if (loadingSpinner) loadingSpinner.style.display = 'flex';
      if (defaultContent) defaultContent.style.display = 'none';
      if (resultsContainer) resultsContainer.style.display = 'block';

      debounceTimeout = setTimeout(() => {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const rootClean = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
        fetch(`${rootClean}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=5`)
          .then(res => res.json())
          .then(data => {
            if (loadingSpinner) loadingSpinner.style.display = 'none';
            const products = data.resources?.results?.products || [];

            if (products.length === 0) {
              resultsContainer.innerHTML = `
                <div class="search-drawer-no-results">
                  <p>No se encontraron productos para "<strong>${escapeHtml(query)}</strong>"</p>
                </div>`;
            } else {
              let html = '<div class="search-drawer-results-list">';
              products.forEach(product => {
                const price = product.price;
                const comparePrice = product.compare_at_price;
                const compareHtml = comparePrice ? `<span class="price-compare">${comparePrice}</span>` : '';
                const title = product.title;
                const imageUrl = product.featured_image?.url || product.image || '//cdn.shopify.com/s/images/themes/product-1.png';

                html += `
                  <div class="search-drawer-product-item">
                    <a href="${product.url}" class="search-drawer-product-link">
                      <div class="search-drawer-product-img-box">
                        <img src="${imageUrl}" alt="${escapeHtml(title)}">
                      </div>
                      <div class="search-drawer-product-info">
                        <h4 class="search-drawer-product-title">${escapeHtml(title)}</h4>
                        <p class="search-drawer-product-price">
                          <span class="price-sale">${price}</span>
                          ${compareHtml}
                        </p>
                      </div>
                    </a>
                  </div>`;
              });
              html += '</div>';
              resultsContainer.innerHTML = html;
            }
          })
          .catch(err => {
            console.error('Error fetching predictive search:', err);
            if (loadingSpinner) loadingSpinner.style.display = 'none';
          });
      }, 350);
    });
  }

  // Predictive search logic for Modal Input
  let modalDebounceTimeout;
  if (modalInput && modal) {
    const resultsContainer = document.getElementById('SearchModalResults');
    const defaultContent = modal.querySelector('.search-modal-default-content');
    const loadingSpinner = modal.querySelector('.search-modal-loading');

    modalInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      clearTimeout(modalDebounceTimeout);

      if (query.length < 2) {
        if (resultsContainer) {
          resultsContainer.innerHTML = '';
          resultsContainer.style.display = 'none';
        }
        if (defaultContent) defaultContent.style.display = 'block';
        if (loadingSpinner) loadingSpinner.style.display = 'none';
        return;
      }

      if (loadingSpinner) loadingSpinner.style.display = 'flex';
      if (defaultContent) defaultContent.style.display = 'none';
      if (resultsContainer) resultsContainer.style.display = 'block';

      modalDebounceTimeout = setTimeout(() => {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const rootClean = rootUrl.endsWith('/') ? rootUrl : rootUrl + '/';
        fetch(`${rootClean}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`)
          .then(res => res.json())
          .then(data => {
            const products = data.resources?.results?.products || [];
            if (products.length === 0) {
              if (loadingSpinner) loadingSpinner.style.display = 'none';
              resultsContainer.innerHTML = `
                <div class="search-modal-no-results">
                  <p>No se encontraron productos para "<strong>${escapeHtml(query)}</strong>"</p>
                </div>`;
            } else {
              const cardPromises = products.map(product => {
                return fetch(`${rootClean}products/${product.handle}?view=card`)
                  .then(res => {
                    if (!res.ok) throw new Error('Card fetch fail');
                    return res.text();
                  })
                  .catch(() => {
                    const price = product.price;
                    const comparePrice = product.compare_at_price;
                    const compareHtml = comparePrice ? `<span class="product-card-compare-price">${comparePrice}</span>` : '';
                    const aspectStyle = modal.getAttribute('data-card-aspect-ratio') || '3 / 4';
                    return `
                      <div class="product-card-item" style="--card-aspect-ratio: ${aspectStyle};">
                        <div class="product-card-image-wrapper">
                          <a href="${product.url}" class="product-card-image-link">
                            <img src="${product.featured_image?.url || product.image || '//cdn.shopify.com/s/images/themes/product-1.png'}" alt="${escapeHtml(product.title)}" class="product-card-img" loading="lazy">
                          </a>
                        </div>
                        <div class="product-card-info align-left">
                          <h4 class="product-card-title">
                            <a href="${product.url}">${escapeHtml(product.title)}</a>
                          </h4>
                          <div class="product-card-pricing">
                            <span class="product-card-price">${price}</span>
                            ${compareHtml}
                          </div>
                        </div>
                      </div>`;
                  });
              });

              Promise.all(cardPromises).then(htmlArray => {
                if (loadingSpinner) loadingSpinner.style.display = 'none';
                resultsContainer.innerHTML = `<div class="search-modal-results-grid">${htmlArray.join('')}</div>`;
                
                // Re-initialize hover states, swatch clicks, and AJAX cart buttons for the new cards!
                if (typeof initProductCards === 'function') initProductCards();
                if (typeof initCartAjaxHandlers === 'function') initCartAjaxHandlers();
              }).catch(err => {
                console.error('Error rendering product cards:', err);
                if (loadingSpinner) loadingSpinner.style.display = 'none';
              });
            }
          })
          .catch(err => {
            console.error('Error fetching predictive search:', err);
            if (loadingSpinner) loadingSpinner.style.display = 'none';
          });
      }, 350);
    });
  }

  function escapeHtml(str) {
    return str
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}

/**
 * Initializes the premium floating Back to Top button
 */
function initBackToTop() {
  const backToTopBtn = document.getElementById('BackToTopBtn');
  if (!backToTopBtn) return;

  // Show button when scrolling down beyond 400px
  window.addEventListener('scroll', () => {
    if (window.pageYOffset > 400) {
      backToTopBtn.classList.add('is-visible');
    } else {
      backToTopBtn.classList.remove('is-visible');
    }
  });

  // Smooth scroll to top when clicked
  backToTopBtn.addEventListener('click', (e) => {
    e.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

/**
 * Initializes the mobile collapsible footer accordions with scrollHeight transitions
 */
function initMobileFooterAccordions() {
  const headers = document.querySelectorAll('.footer-col-header');

  // Run once initially to set the max-height of already open containers on mobile
  const setInitialHeights = () => {
    if (window.innerWidth > 768) return;
    headers.forEach(header => {
      const content = header.nextElementSibling;
      if (header.classList.contains('is-open') && content) {
        content.style.maxHeight = content.scrollHeight + 'px';
      } else if (content) {
        content.style.maxHeight = '0px';
      }
    });
  };

  // Set heights initially
  setTimeout(setInitialHeights, 150);

  // Handle clicks
  headers.forEach(header => {
    header.addEventListener('click', (e) => {
      if (window.innerWidth > 768) return;
      e.preventDefault();

      const content = header.nextElementSibling;
      const isOpen = header.classList.contains('is-open');

      if (isOpen) {
        header.classList.remove('is-open');
        header.setAttribute('aria-expanded', 'false');
        if (content) {
          content.classList.remove('is-open');
          content.style.maxHeight = '0px';
        }
      } else {
        header.classList.add('is-open');
        header.setAttribute('aria-expanded', 'true');
        if (content) {
          content.classList.add('is-open');
          content.style.maxHeight = content.scrollHeight + 'px';
        }
      }
    });
  });

  // Re-eval on resize (e.g. tablet rotation)
  window.addEventListener('resize', () => {
    if (window.innerWidth > 768) {
      headers.forEach(header => {
        const content = header.nextElementSibling;
        if (content) content.style.maxHeight = '';
      });
    } else {
      setInitialHeights();
    }
  });
}

/**
 * Initializes interactive behaviors for premium product cards
 */
function initProductCards() {
  // 1. Wishlist click feedback
  const wishlistBtns = document.querySelectorAll('.product-card-action-btn.wishlist-btn, .list-action-btn.wishlist-btn');
  wishlistBtns.forEach(btn => {
    if (btn.dataset.wishlistInitialized) return;
    btn.dataset.wishlistInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const card = btn.closest('.product-card-item');
      if (!card) return;

      const productData = {
        handle: card.dataset.productHandle,
        id: card.dataset.productId,
        title: card.dataset.productTitle,
        price: card.dataset.productPrice,
        compare_price: card.dataset.productComparePrice || null,
        image: card.dataset.productImage,
        url: card.dataset.productUrl,
        variant_id: card.dataset.variantId
      };

      if (window.themeWishlist) {
        window.themeWishlist.toggle(productData);
      }
    });
  });

  // 2. Compare click feedback (Deactivated but left safe)
  const compareBtns = document.querySelectorAll('.product-card-action-btn.compare-btn');
  compareBtns.forEach(btn => {
    if (btn.dataset.compareInitialized) return;
    btn.dataset.compareInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
    });
  });

  // 3. Color Swatch Click Handler
  const swatchBtns = document.querySelectorAll('.product-card-item .swatch-btn');
  swatchBtns.forEach(btn => {
    if (btn.dataset.swatchInitialized) return;
    btn.dataset.swatchInitialized = 'true';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const card = btn.closest('.product-card-item');
      if (!card) return;

      // Toggle active class among siblings
      const row = btn.closest('.product-card-swatches-row');
      if (row) {
        row.querySelectorAll('.swatch-btn').forEach(s => s.classList.remove('is-active'));
        
        // Manage swatch-selected class to handle hover state correctly
        const firstSwatch = row.querySelector('.swatch-btn');
        if (btn === firstSwatch) {
          card.classList.remove('swatch-selected');
        } else {
          card.classList.add('swatch-selected');
        }
      }
      btn.classList.add('is-active');

      // Update image with smooth fade transition
      const imageUrl = btn.getAttribute('data-image-url');
      const img = card.querySelector('.product-card-img:not(.product-card-img--secondary)');
      if (imageUrl && img && img.src !== imageUrl) {
        img.style.opacity = '0';
        setTimeout(() => {
          img.src = imageUrl;
          if (img.srcset) {
            img.srcset = ''; // Clear srcset to force showing the new specific image
          }
          img.onload = () => {
            img.style.opacity = '';
          };
          // Fallback if onload doesn't fire
          setTimeout(() => {
            img.style.opacity = '';
          }, 300);
        }, 150);
      }

      // Update link URL with variant ID
      const variantId = btn.getAttribute('data-variant-id');
      const links = card.querySelectorAll('.product-card-image-link, .product-card-title a');
      links.forEach(link => {
        let baseHref = link.getAttribute('data-base-href') || link.getAttribute('href');
        if (!link.getAttribute('data-base-href')) {
          link.setAttribute('data-base-href', baseHref);
        }
        // Strip out existing variant parameter if any
        baseHref = baseHref.split('?')[0];
        if (variantId) {
          link.setAttribute('href', `${baseHref}?variant=${variantId}`);
        } else {
          link.setAttribute('href', baseHref);
        }
      });
    });
  });
}

/**
 * Helper function to show a sleek temporary toast notification
 */
function showToast(message) {
  let toast = document.querySelector('.theme-toast');
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'theme-toast';
    document.body.appendChild(toast);
  }
  toast.textContent = message;
  toast.classList.add('is-active');

  // Reset existing timeouts
  if (window.toastTimeout) clearTimeout(window.toastTimeout);

  window.toastTimeout = setTimeout(() => {
    toast.classList.remove('is-active');
  }, 2200);
}

/**
 * Initializes interactive filters, accordions and layout grid switches for the Collection page
 */
function initCollectionFilters() {
  // 1. Sidebar Collapsible Accordions
  const filterHeaders = document.querySelectorAll('.filter-group-header');
  filterHeaders.forEach(header => {
    // Set initial maxHeight for open accordions so they transitions smoothly
    const content = header.nextElementSibling;
    if (content && !header.classList.contains('is-closed')) {
      content.style.maxHeight = content.scrollHeight + 'px';
    }

    header.addEventListener('click', (e) => {
      e.preventDefault();
      const content = header.nextElementSibling;
      const isClosed = header.classList.toggle('is-closed');
      header.setAttribute('aria-expanded', isClosed ? 'false' : 'true');

      if (content) {
        if (isClosed) {
          content.style.maxHeight = '0px';
          content.style.opacity = '0';
          content.style.marginTop = '0px';
        } else {
          content.style.maxHeight = content.scrollHeight + 'px';
          content.style.opacity = '1';
          content.style.marginTop = '0.8rem';
        }
      }
    });
  });

  // 2. Grid Column Layout Switches
  const gridSwitches = document.querySelectorAll('.grid-switch-btn');
  const productsGrid = document.getElementById('CollectionProductsGrid');

  if (productsGrid && gridSwitches.length > 0) {
    gridSwitches.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        // Remove active class from all switches
        gridSwitches.forEach(s => s.classList.remove('is-active'));

        // Add active class to clicked switch
        btn.classList.add('is-active');

        // Read columns count
        const cols = btn.getAttribute('data-cols');

        // Reset classes
        productsGrid.classList.remove('cols-1', 'cols-2', 'cols-3', 'cols-4');
        productsGrid.classList.add(`cols-${cols}`);
      });
    });

    // Automatically set default view to 2 columns on mobile devices initially
    if (window.innerWidth <= 768) {
      productsGrid.classList.remove('cols-1', 'cols-2', 'cols-3', 'cols-4');
      productsGrid.classList.add('cols-2');

      gridSwitches.forEach(s => s.classList.remove('is-active'));
      const mobileDefaultSwitch = document.querySelector('.grid-switch-btn.cols-2');
      if (mobileDefaultSwitch) {
        mobileDefaultSwitch.classList.add('is-active');
      }
    }
  }

  // 3. Custom Premium Sorting Dropdown Selector
  const customSelect = document.getElementById('CustomSortDropdown');
  if (customSelect) {
    const trigger = customSelect.querySelector('.custom-select-trigger');
    const label = customSelect.querySelector('.custom-select-label');
    const options = customSelect.querySelectorAll('.custom-select-option');
    const nativeSelect = document.getElementById('SortBy');

    if (trigger && options.length > 0 && nativeSelect) {
      // Toggle dropdown open/close
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const isOpen = customSelect.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });

      // Handle selecting an option
      options.forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Remove selected class from all options
          options.forEach(opt => opt.classList.remove('is-selected'));

          // Add selected class to current option
          option.classList.add('is-selected');

          // Read value and label
          const val = option.getAttribute('data-value');
          const optionText = option.querySelector('span').textContent;

          // Update trigger label
          label.textContent = optionText;

          // Sync with hidden native select
          nativeSelect.value = val;

          // Trigger change event to fire Shopify's dynamic reactive sorting
          const event = new Event('change', { bubbles: true });
          nativeSelect.dispatchEvent(event);

          // Close custom dropdown
          customSelect.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        });
      });

      // Close dropdown if clicking anywhere else outside
      document.addEventListener('click', (e) => {
        if (!customSelect.contains(e.target)) {
          customSelect.classList.remove('is-open');
          trigger.setAttribute('aria-expanded', 'false');
        }
      });
    }
  }

  // 4. Double Knob Price Slider Calculations & Synchronization
  const dualSliders = document.querySelectorAll('.dual-range-slider');
  dualSliders.forEach(slider => {
    const rangeMin = slider.querySelector('.range-min');
    const rangeMax = slider.querySelector('.range-max');
    const track = slider.querySelector('.slider-track');
    const container = slider.closest('.price-slider-container');
    if (!container) return;
    const inputMin = container.querySelector('.price-input-min');
    const inputMax = container.querySelector('.price-input-max');

    if (!rangeMin || !rangeMax || !track || !inputMin || !inputMax) return;

    const rangeMaxVal = parseFloat(rangeMin.max) || 200;

    const updateSliderUI = () => {
      let minVal = parseFloat(rangeMin.value);
      let maxVal = parseFloat(rangeMax.value);

      // Prevent thumbs from crossing each other
      if (maxVal - minVal < 0) {
        rangeMin.value = maxVal;
        minVal = maxVal;
      }

      // Sync display inputs
      inputMin.value = minVal;
      inputMax.value = maxVal;

      // Update tracks highlight width and offsets
      const leftPct = (minVal / rangeMaxVal) * 100;
      const rightPct = 100 - (maxVal / rangeMaxVal) * 100;
      track.style.left = leftPct + '%';
      track.style.right = rightPct + '%';
    };

    // Listen to changes on range handles (continuous update)
    rangeMin.addEventListener('input', updateSliderUI);
    rangeMax.addEventListener('input', updateSliderUI);

    // Initial load
    updateSliderUI();
  });

  // 5. Automatic form submission for Filter Forms
  const filtersForm = document.getElementById('CollectionFiltersForm');
  if (filtersForm) {
    // Bridge external SortBy select changes to the filter form
    const nativeSort = document.getElementById('SortBy');
    if (nativeSort) {
      nativeSort.addEventListener('change', () => {
        const sortInput = filtersForm.querySelector('input[name="sort_by"]');
        if (sortInput) {
          sortInput.value = nativeSort.value;
        }
        filtersForm.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // We listen to the "change" event! Checkboxes & standard select trigger "change"
    // Price range inputs will also trigger "change" when the user RELEASES the slider handles!
    filtersForm.addEventListener('change', () => {
      // Sync sort_by if SortBy native select exists and has a value
      const sortInput = filtersForm.querySelector('input[name="sort_by"]');
      if (nativeSort && sortInput) {
        sortInput.value = nativeSort.value;
      }

      // Create parameters object from Form data
      const formData = new FormData(filtersForm);
      const params = new URLSearchParams();

      for (const [key, val] of formData.entries()) {
        if (val) {
          params.append(key, val);
        }
      }

      // Automatically reload page via AJAX with the filters applied!
      const targetSearch = params.toString();
      const targetUrl = window.location.pathname + (targetSearch ? '?' + targetSearch : '');
      updateCollectionPage(targetUrl);
    });
  }

  // 6. Mobile Drawer Open/Close Interaction
  const mobileTrigger = document.getElementById('MobileFiltersTrigger');
  const drawerSidebar = document.querySelector('.collection-filters-sidebar');
  const drawerOverlay = document.getElementById('CollectionDrawerOverlay');
  const drawerCloseBtn = document.getElementById('MobileDrawerClose');

  if (mobileTrigger && drawerSidebar && drawerOverlay) {
    const openDrawer = () => {
      drawerSidebar.classList.add('is-open');
      drawerOverlay.classList.add('active');
      document.body.style.overflow = 'hidden'; // Prevent body scroll while viewing drawer
    };

    const closeDrawer = () => {
      drawerSidebar.classList.remove('is-open');
      drawerOverlay.classList.remove('active');
      document.body.style.overflow = ''; // Restore body scroll
    };

    mobileTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      openDrawer();
    });

    if (drawerCloseBtn) {
      drawerCloseBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeDrawer();
      });
    }

    if (!drawerOverlay.dataset.overlayListenerAdded) {
      drawerOverlay.addEventListener('click', closeDrawer);
      drawerOverlay.dataset.overlayListenerAdded = 'true';
    }
  }

  // 7. AJAX Pagination Click Handlers
  const paginationLinks = document.querySelectorAll('.pagination-item');
  paginationLinks.forEach(link => {
    if (link.tagName === 'A') {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const targetUrl = link.getAttribute('href');
        if (targetUrl && targetUrl !== '#') {
          updateCollectionPage(targetUrl);
        }
      });
    }
  });

  // 8. Global Escape Key Listener for Filters Drawer
  if (!window.filtersEscapeListenerAdded) {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        const activeSidebar = document.querySelector('.collection-filters-sidebar.is-open');
        if (activeSidebar) {
          activeSidebar.classList.remove('is-open');
          const overlay = document.getElementById('CollectionDrawerOverlay');
          if (overlay) overlay.classList.remove('active');
          document.body.style.overflow = '';
        }
      }
    });
    window.filtersEscapeListenerAdded = true;
  }
}

/**
 * Updates the collection page dynamically using AJAX
 * @param {string} targetUrl The target URL containing filter search parameters
 * @param {boolean} skipPushState Set to true to avoid pushing history state (e.g. for popstate)
 */
function updateCollectionPage(targetUrl, skipPushState = false) {
  const wrapper = document.querySelector('.collection-page-wrapper');
  if (!wrapper) return;

  // Visual feedback: reduce grid opacity and disable mouse interactions
  const grid = document.getElementById('CollectionProductsGrid');
  if (grid) {
    grid.style.opacity = '0.5';
    grid.style.pointerEvents = 'none';
  }

  fetch(targetUrl)
    .then(response => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.text();
    })
    .then(html => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const newWrapperContent = doc.querySelector('.collection-page-wrapper');
      if (newWrapperContent) {
        const oldDrawer = document.querySelector('.collection-filters-sidebar');
        const newDrawer = doc.querySelector('.collection-filters-sidebar');
        const oldProducts = document.querySelector('.collection-products-panel');
        const newProducts = doc.querySelector('.collection-products-panel');

        // Capture scroll position of the drawer before swap
        const drawerScrollTop = oldDrawer ? oldDrawer.scrollTop : 0;

        // Save accordion states by group ID
        const accordionStates = {};
        document.querySelectorAll('.filter-group-wrapper').forEach(group => {
          const groupId = group.id;
          const header = group.querySelector('.filter-group-header');
          if (groupId && header) {
            accordionStates[groupId] = !header.classList.contains('is-closed');
          }
        });

        if (oldDrawer && newDrawer && oldProducts && newProducts) {
          // Swap inner content of filters sidebar and products panel separately
          // to preserve the outer elements, preventing transition triggers or losing open class.
          oldDrawer.innerHTML = newDrawer.innerHTML;
          oldDrawer.scrollTop = drawerScrollTop;
          oldProducts.innerHTML = newProducts.innerHTML;
        } else {
          // Fallback to swapping the entire wrapper content
          wrapper.innerHTML = newWrapperContent.innerHTML;
        }

        // Restore accordion states
        document.querySelectorAll('.filter-group-wrapper').forEach(group => {
          const groupId = group.id;
          if (groupId && groupId in accordionStates) {
            const isOpen = accordionStates[groupId];
            const header = group.querySelector('.filter-group-header');
            const content = group.querySelector('.filter-group-content');
            if (header && content) {
              if (isOpen) {
                header.classList.remove('is-closed');
                header.setAttribute('aria-expanded', 'true');
                content.style.maxHeight = '';
                content.style.opacity = '';
                content.style.marginTop = '';
              } else {
                header.classList.add('is-closed');
                header.setAttribute('aria-expanded', 'false');
                content.style.maxHeight = '0px';
                content.style.opacity = '0';
                content.style.marginTop = '0px';
              }
            }
          }
        });

        if (!skipPushState) {
          history.pushState({ path: targetUrl }, '', targetUrl);
        }

        // Re-initialize event listeners for all elements inside the swapped content
        initCollectionFilters();
        initProductCards();
        initCartAjaxHandlers();
      }
    })
    .catch(error => {
      console.error('Error fetching collection filters:', error);
      if (grid) {
        grid.style.opacity = '1';
        grid.style.pointerEvents = 'auto';
      }
    });
}

// Handle browser navigation (back/forward arrows)
if (!window.filtersPopstateListenerAdded) {
  window.addEventListener('popstate', () => {
    if (document.querySelector('.collection-page-wrapper')) {
      updateCollectionPage(window.location.href, true);
    }
  });
  window.filtersPopstateListenerAdded = true;
}

/**
 * Initializes the AJAX Add to Cart and Line Item removal event listeners
 */
function initCartAjaxHandlers() {
  // Select all add-to-cart buttons
  const addBtns = document.querySelectorAll('.product-card-add-btn, .product-card-add-btn-icon-always, .rec-card-add-btn-ajax, .list-action-btn.cart-btn');

  addBtns.forEach(btn => {
    // Prevent duplicate listeners on AJAX buttons
    if (btn.dataset.ajaxInitialized) return;
    btn.dataset.ajaxInitialized = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const variantId = btn.getAttribute('data-variant-id');
      if (!variantId) {
        console.error('No variant ID found on add-to-cart button');
        return;
      }

      const isIconOnly = btn.classList.contains('product-card-add-btn-icon-always');
      let originalContent;

      // Loading state feedback
      if (isIconOnly) {
        originalContent = btn.innerHTML;
        btn.innerHTML = `
          <svg style="animation: spin 1s linear infinite;" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" width="20" height="20">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity: 0.25; fill: none;"></circle>
            <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;
      } else {
        originalContent = btn.textContent;
        btn.textContent = 'Agregando...';
      }

      btn.disabled = true;

      // Shopify AJAX Add to Cart POST request
      fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          'id': variantId,
          'quantity': 1
        }).toString()
      })
        .then(res => {
          if (!res.ok) {
            throw new Error('Error adding product to cart');
          }
          return res.json();
        })
        .then(item => {
          // Success state feedback
          if (isIconOnly) {
            btn.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5" width="20" height="20">
              <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          `;
          } else {
            btn.textContent = '¡Añadido!';
          }

          // Dynamically refresh drawer HTML and open it or refresh cart page if on main cart page!
          if (window.refreshCartPageContent) {
            window.refreshCartPageContent();
          } else {
            refreshCartDrawer(true); // pass true to open the drawer after refreshing
          }

          // Revert button status back to normal after a short delay
          setTimeout(() => {
            if (isIconOnly) {
              btn.innerHTML = originalContent;
            } else {
              btn.textContent = originalContent;
            }
            btn.disabled = false;
          }, 1500);
        })
        .catch(err => {
          console.error('AJAX cart error:', err);
          showToast('Error al añadir al carrito. Por favor, inténtelo de nuevo.');
          if (isIconOnly) {
            btn.innerHTML = originalContent;
          } else {
            btn.textContent = originalContent;
          }
          btn.disabled = false;
        });
    });
  });

  // Handle Dynamic Line Item Removal
  const removeBtns = document.querySelectorAll('.cart-item-remove-btn');
  removeBtns.forEach(btn => {
    if (btn.dataset.ajaxInitialized) return;
    btn.dataset.ajaxInitialized = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const line = btn.getAttribute('data-line');
      if (!line) return;

      const itemRow = btn.closest('.cart-drawer-item');
      if (itemRow) {
        // Smooth collapse and slide out animation
        itemRow.style.transition = 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        itemRow.style.opacity = '0';
        itemRow.style.transform = 'translateX(50px)';
        itemRow.style.pointerEvents = 'none';
        itemRow.style.maxHeight = itemRow.offsetHeight + 'px';
        setTimeout(() => {
          itemRow.style.maxHeight = '0px';
          itemRow.style.paddingTop = '0px';
          itemRow.style.paddingBottom = '0px';
          itemRow.style.borderBottomWidth = '0px';
          itemRow.style.marginBottom = '0px';
        }, 100);
      }

      // Fire fetch request immediately in parallel to the animation, requesting the cart-drawer section
      const fetchPromise = fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          'line': parseInt(line),
          'quantity': 0,
          'sections': 'cart-drawer'
        })
      }).then(res => {
        if (!res.ok) throw new Error('Error removing item');
        return res.json();
      });

      // Wait 300ms for the animation to complete, then process the fetch response
      setTimeout(() => {
        fetchPromise
          .then(cart => {
            refreshCartDrawer(false, cart);
            if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
              window.refreshCartPageContent();
            }
          })
          .catch(err => {
            console.error('AJAX remove error:', err);
            if (itemRow) {
              itemRow.style.opacity = '';
              itemRow.style.pointerEvents = '';
              itemRow.style.transform = '';
              itemRow.style.maxHeight = '';
              itemRow.style.paddingTop = '';
              itemRow.style.paddingBottom = '';
              itemRow.style.borderBottomWidth = '';
              itemRow.style.marginBottom = '';
            }
            refreshCartDrawer(false); // Fallback refresh
          });
      }, 300);
    });
  });

  // Handle Drawer Quantity Selectors
  const drawerSelectors = document.querySelectorAll('#CartDrawer .cart-qty-selector');
  drawerSelectors.forEach(selector => {
    if (selector.dataset.qtyInitialized) return;
    selector.dataset.qtyInitialized = 'true';

    const minusBtn = selector.querySelector('.qty-minus');
    const plusBtn = selector.querySelector('.qty-plus');
    const input = selector.querySelector('.qty-input');
    if (!input) return;

    const lineIndex = input.getAttribute('data-line');

    const updateQuantity = (newVal) => {
      if (newVal < 0) newVal = 0;

      // Check inventory limits
      const invManagement = input.getAttribute('data-inventory-management');
      const invPolicy = input.getAttribute('data-inventory-policy');
      const invQuantity = parseInt(input.getAttribute('data-inventory-quantity'));

      if (invManagement && invPolicy !== 'continue' && !isNaN(invQuantity)) {
        if (newVal > invQuantity) {
          newVal = invQuantity;
          input.value = newVal;
          if (typeof showToast !== 'undefined') {
            showToast('No hay más stock disponible.');
          } else {
            alert('No hay más stock disponible.');
          }
          return;
        }
      }

      input.value = newVal;

      // Show loader
      selector.classList.add('is-loading');

      const itemRow = selector.closest('.cart-drawer-item');
      let delay = 0;
      if (newVal === 0 && itemRow) {
        delay = 300;
        // If removing, animate it out smoothly
        itemRow.style.transition = 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)';
        itemRow.style.opacity = '0';
        itemRow.style.transform = 'translateX(50px)';
        itemRow.style.pointerEvents = 'none';
        itemRow.style.maxHeight = itemRow.offsetHeight + 'px';
        setTimeout(() => {
          itemRow.style.maxHeight = '0px';
          itemRow.style.paddingTop = '0px';
          itemRow.style.paddingBottom = '0px';
          itemRow.style.borderBottomWidth = '0px';
          itemRow.style.marginBottom = '0px';
        }, 100);
      }

      // Fire fetch request immediately in parallel to the animation, requesting the cart-drawer section
      const fetchPromise = fetch((window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          line: parseInt(lineIndex),
          quantity: newVal,
          sections: 'cart-drawer'
        })
      }).then(res => {
        if (!res.ok) throw new Error('Error updating drawer cart qty');
        return res.json();
      });

      const handleResponse = () => {
        fetchPromise
          .then(cart => {
            refreshCartDrawer(false, cart);
            if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
              window.refreshCartPageContent();
            }
          })
          .catch(err => {
            console.error('AJAX qty update error:', err);
            refreshCartDrawer(false);
          });
      };

      if (delay > 0) {
        setTimeout(handleResponse, delay);
      } else {
        handleResponse();
      }
    };

    if (minusBtn) {
      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentVal = parseInt(input.value) || 0;
        if (currentVal > 0) {
          updateQuantity(currentVal - 1);
        }
      });
    }

    if (plusBtn) {
      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentVal = parseInt(input.value) || 0;
        updateQuantity(currentVal + 1);
      });
    }

    input.addEventListener('change', () => {
      const newVal = parseInt(input.value);
      if (!isNaN(newVal)) {
        updateQuantity(newVal);
      }
    });
  });
}

/**
 * Dynamically refreshes the Shopify Cart Drawer section via the Section Rendering API
 */
function refreshCartDrawer(openDrawerAfter = false, preRenderedHtml = null) {
  const sectionId = 'cart-drawer';
  const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';

  const currentDrawer = document.getElementById('CartDrawer');
  
  // If opening requested, open immediately to start the CSS slide-in animation on stable DOM
  if (openDrawerAfter && currentDrawer) {
    currentDrawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const recPanel = document.getElementById('CartDrawerRecs');
    if (recPanel) recPanel.classList.remove('is-closed');
  }

  const wasOpen = currentDrawer && currentDrawer.classList.contains('is-open');

  const oldSection = document.getElementById('shopify-section-cart-drawer');
  if (oldSection && wasOpen) {
    oldSection.classList.add('is-updating');
  }

  const handleJson = (json) => {
    const html = json[sectionId] || (json.sections && json.sections[sectionId]);
    if (!html) {
      if (oldSection) oldSection.classList.remove('is-updating');
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const newSection = doc.getElementById('shopify-section-cart-drawer') || doc.querySelector('.shopify-section');

    if (newSection && oldSection) {
      const oldDrawer = document.getElementById('CartDrawer');
      const oldRecs = document.getElementById('CartDrawerRecs');
      const oldMain = document.getElementById('CartDrawerMain');
      const oldWrapper = oldSection.querySelector('.cart-drawer-wrapper');

      const newDrawer = newSection.querySelector('#CartDrawer');
      const newRecs = newSection.querySelector('#CartDrawerRecs');
      const newMain = newSection.querySelector('#CartDrawerMain');
      const newWrapper = newSection.querySelector('.cart-drawer-wrapper');

      const canUpdateInPlace = oldDrawer && newDrawer && oldRecs && newRecs && oldMain && newMain && oldWrapper && newWrapper;

      if (canUpdateInPlace) {
        // Capture active tab state before update
        const activeTab = oldDrawer.querySelector('.cart-drawer-tab-btn.is-active');
        const activeTabId = activeTab ? activeTab.getAttribute('data-tab') : null;

        // Swap panel contents in place to preserve parent DOM nodes (transition properties remain stable)
        oldRecs.innerHTML = newRecs.innerHTML;
        oldMain.innerHTML = newMain.innerHTML;

        // Sync outer wrapper classes (color scheme, empty state)
        oldWrapper.className = newWrapper.className;

        // Sync discount attribute
        if (newDrawer.hasAttribute('data-active-discount')) {
          oldDrawer.setAttribute('data-active-discount', newDrawer.getAttribute('data-active-discount'));
        } else {
          oldDrawer.removeAttribute('data-active-discount');
        }

        // Restore active tab
        if (activeTabId) {
          const restoredTab = oldMain.querySelector(`.cart-drawer-tab-btn[data-tab="${activeTabId}"]`);
          const restoredPanel = oldMain.querySelector(`#AddonPanel-${activeTabId}`);
          if (restoredTab) restoredTab.classList.add('is-active');
          if (restoredPanel) restoredPanel.classList.add('is-active');
        }

        // Re-initialize drawer interactivity
        initCartDrawer();
        initCartAjaxHandlers();
        initCartCoupon();
        updateCartBadges();

        // Ensure visual classes are in correct state
        if (wasOpen || openDrawerAfter) {
          oldDrawer.classList.add('is-open');
        }
      } else {
        // Fallback to full swap if elements are missing
        const activeTab = oldDrawer ? oldDrawer.querySelector('.cart-drawer-tab-btn.is-active') : null;
        const activeTabId = activeTab ? activeTab.getAttribute('data-tab') : null;

        if (wasOpen || openDrawerAfter) {
          if (newDrawer) newDrawer.classList.add('is-open');
        }

        if (activeTabId && newSection) {
          const newTab = newSection.querySelector(`.cart-drawer-tab-btn[data-tab="${activeTabId}"]`);
          const newPanel = newSection.querySelector(`#AddonPanel-${activeTabId}`);
          if (newTab) newTab.classList.add('is-active');
          if (newPanel) newPanel.classList.add('is-active');
        }

        oldSection.innerHTML = newSection.innerHTML;

        initCartDrawer();
        initCartAjaxHandlers();
        initCartCoupon();
        updateCartBadges();

        const restoredDrawer = document.getElementById('CartDrawer');
        if (restoredDrawer) {
          if (wasOpen || openDrawerAfter) {
            requestAnimationFrame(() => {
              restoredDrawer.classList.add('is-open');
              document.body.style.overflow = 'hidden';
              const recPanel = document.getElementById('CartDrawerRecs');
              if (recPanel) recPanel.classList.remove('is-closed');
            });
          }
        }
      }

      // Fade back in smoothly
      setTimeout(() => {
        oldSection.classList.remove('is-updating');
      }, 50);
    } else {
      if (oldSection) oldSection.classList.remove('is-updating');
    }
  };

  if (preRenderedHtml) {
    handleJson(preRenderedHtml);
  } else {
    fetch(`${rootUrl}?sections=${sectionId}`)
      .then(res => res.json())
      .then(json => {
        handleJson(json);
      })
      .catch(err => {
        console.error('Error refreshing cart drawer section:', err);
        if (oldSection) oldSection.classList.remove('is-updating');
      });
  }
}

/**
 * Fetches the current cart state and updates all header and sticky badge counts dynamically
 */
function updateCartBadges() {
  const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';

  fetch(rootUrl + 'cart.js')
    .then(res => res.json())
    .then(cart => {
      const count = cart.item_count;

      const headerBadges = document.querySelectorAll('.cart-badge, .cart-count-badge, #CartCount');
      const stickyBadges = document.querySelectorAll('#MobileStickyCartCount');

      headerBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });

      stickyBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });
    })
    .catch(err => {
      console.error('Error updating cart badges:', err);
    });
}

function showTemporaryBanner(msgContainer, savedCoupon, isDrawer) {
  if (!msgContainer) return;
  
  if (msgContainer.fadeTimeout) clearTimeout(msgContainer.fadeTimeout);
  if (msgContainer.hideTimeout) clearTimeout(msgContainer.hideTimeout);

  const styleMargin = isDrawer ? 'margin-bottom: 12px;' : 'margin-top: 10px;';
  msgContainer.innerHTML = `
    <div class="coupon-activated-banner" style="display: flex; align-items: center; justify-content: space-between; gap: 8px; ${styleMargin} padding: 10px 14px; background-color: #f0fdf4; border: 1px solid #bbf7d0; border-radius: max(0px, calc(var(--layout-border-radius) - 12px)); transition: opacity 0.5s cubic-bezier(0.16, 1, 0.3, 1), transform 0.5s cubic-bezier(0.16, 1, 0.3, 1); transform: translateY(-10px); opacity: 0;">
      <span style="display: flex; align-items: center; gap: 6px; color: #16a34a; font-size: 0.85rem; font-weight: 500;">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" width="16" height="16" style="flex-shrink: 0;">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        Cupón <strong>"${savedCoupon}"</strong> activado
      </span>
      <button type="button" class="clear-coupon-btn" style="background: none; border: none; color: #dc2626; cursor: pointer; font-size: 0.8rem; font-weight: 600; padding: 2px 6px; text-decoration: underline; transition: color 0.2s;" onmouseover="this.style.color='#991b1b'" onmouseout="this.style.color='#dc2626'">Quitar</button>
    </div>
  `;
  msgContainer.style.display = 'block';
  
  const banner = msgContainer.querySelector('.coupon-activated-banner');
  if (banner) {
    void banner.offsetWidth;
    banner.style.opacity = '1';
    banner.style.transform = 'translateY(0)';

    msgContainer.fadeTimeout = setTimeout(() => {
      banner.style.opacity = '0';
      banner.style.transform = 'translateY(-10px)';
      msgContainer.hideTimeout = setTimeout(() => {
        msgContainer.style.display = 'none';
        msgContainer.innerHTML = '';
      }, 500);
    }, 4000);
  }
}

function updateCouponUI() {
  const drawerEl = document.getElementById('CartDrawer');
  const pageEl = document.querySelector('.cart-page-wrapper');
  
  let serverDiscount = null;
  if (drawerEl && drawerEl.hasAttribute('data-active-discount')) {
    serverDiscount = drawerEl.getAttribute('data-active-discount') || '';
  } else if (pageEl && pageEl.hasAttribute('data-active-discount')) {
    serverDiscount = pageEl.getAttribute('data-active-discount') || '';
  }
  
  if (serverDiscount !== null) {
    const currentSessionCoupon = sessionStorage.getItem('cart_discount_code') || '';
    if (!serverDiscount && currentSessionCoupon) {
      sessionStorage.removeItem('cart_discount_code');
      sessionStorage.removeItem('last_shown_coupon');
    } else if (serverDiscount && serverDiscount !== currentSessionCoupon) {
      sessionStorage.setItem('cart_discount_code', serverDiscount);
    }
  }

  const savedCoupon = sessionStorage.getItem('cart_discount_code') || '';
  const lastShownCoupon = sessionStorage.getItem('last_shown_coupon') || '';
  const drawerCoupon = document.getElementById('CartCoupon');
  const pageCoupon = document.getElementById('CartPageCoupon');
  const drawerMsg = document.getElementById('CartDrawerCouponMessage');
  const pageMsg = document.getElementById('CartPageCouponMessage');

  // Sync inputs
  if (drawerCoupon) drawerCoupon.value = savedCoupon;
  if (pageCoupon) pageCoupon.value = savedCoupon;

  // Only show temporary confirmation banner if the coupon is newly activated
  if (savedCoupon !== lastShownCoupon) {
    if (savedCoupon) {
      if (drawerMsg) showTemporaryBanner(drawerMsg, savedCoupon, true);
      if (pageMsg) showTemporaryBanner(pageMsg, savedCoupon, false);
      sessionStorage.setItem('last_shown_coupon', savedCoupon);
    } else {
      sessionStorage.removeItem('last_shown_coupon');
      if (drawerMsg) {
        drawerMsg.style.display = 'none';
        drawerMsg.innerHTML = '';
      }
      if (pageMsg) {
        pageMsg.style.display = 'none';
        pageMsg.innerHTML = '';
      }
    }
  }

  // Sync applied coupon badges on cart and drawer products
  const badges = document.querySelectorAll('.applied-coupon-badge');
  badges.forEach(badge => {
    if (savedCoupon) {
      const codePlaceholder = badge.querySelector('.coupon-code-placeholder');
      if (codePlaceholder) codePlaceholder.textContent = savedCoupon;
      badge.style.display = 'inline-flex';
      badge.style.opacity = '0';
      void badge.offsetWidth;
      badge.style.opacity = '1';
    } else {
      badge.style.opacity = '0';
      setTimeout(() => {
        badge.style.display = 'none';
      }, 300);
    }
  });
}

/**
 * Restores and synchronizes discount code inputs across drawer and cart page,
 * persisting active coupons in sessionStorage and showing alerts.
 */
function initCartCoupon() {
  const drawerCoupon = document.getElementById('CartCoupon');
  const pageCoupon = document.getElementById('CartPageCoupon');

  // Synchronize inputs and status banners
  updateCouponUI();

  // Bind drawer coupon Save button
  const drawerSaveBtn = document.querySelector('#AddonPanel-coupon .overlay-btn-save');
  if (drawerSaveBtn && !drawerSaveBtn.dataset.couponBound) {
    drawerSaveBtn.dataset.couponBound = 'true';
    drawerSaveBtn.addEventListener('click', () => {
      if (drawerCoupon) {
        const val = drawerCoupon.value.trim();
        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        if (val) {
          sessionStorage.setItem('cart_discount_code', val);
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: val })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón activado: ' + val);
              updateCouponUI();
              if (window.refreshCartDrawer) window.refreshCartDrawer(false);
            });
        } else {
          sessionStorage.removeItem('cart_discount_code');
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: '' })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón eliminado');
              updateCouponUI();
              if (window.refreshCartDrawer) window.refreshCartDrawer(false);
            });
        }
      }
    });
  }

  // Bind page coupon Apply button
  const pageApplyBtn = document.querySelector('.coupon-apply-btn');
  if (pageApplyBtn && !pageApplyBtn.dataset.couponBound) {
    pageApplyBtn.dataset.couponBound = 'true';
    pageApplyBtn.addEventListener('click', (e) => {
      e.preventDefault();
      if (pageCoupon) {
        const val = pageCoupon.value.trim();
        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        if (val) {
          sessionStorage.setItem('cart_discount_code', val);
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: val })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón activado: ' + val);
              updateCouponUI();
              if (window.refreshCartPageContent) window.refreshCartPageContent();
            });
        } else {
          sessionStorage.removeItem('cart_discount_code');
          fetch(rootUrl + 'cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ discount: '' })
          })
            .then(res => res.json())
            .then(() => {
              showToast('Cupón eliminado');
              updateCouponUI();
              if (window.refreshCartPageContent) window.refreshCartPageContent();
            });
        }
      }
    });
  }

  // Add Enter key handlers for coupon inputs
  if (pageCoupon && !pageCoupon.dataset.enterBound) {
    pageCoupon.dataset.enterBound = 'true';
    pageCoupon.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (pageApplyBtn) pageApplyBtn.click();
      }
    });
  }

  if (drawerCoupon && !drawerCoupon.dataset.enterBound) {
    drawerCoupon.dataset.enterBound = 'true';
    drawerCoupon.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        if (drawerSaveBtn) drawerSaveBtn.click();
      }
    });
  }

  // Bind remove coupon button in summary
  const removeSummaryBtns = document.querySelectorAll('.remove-coupon-btn-summary');
  removeSummaryBtns.forEach(removeSummaryBtn => {
    if (removeSummaryBtn && !removeSummaryBtn.dataset.removeBound) {
      removeSummaryBtn.dataset.removeBound = 'true';
      removeSummaryBtn.addEventListener('click', (e) => {
        e.preventDefault();
        sessionStorage.removeItem('cart_discount_code');
        sessionStorage.removeItem('last_shown_coupon');
        const drawerCouponInput = document.getElementById('CartCoupon');
        const pageCouponInput = document.getElementById('CartPageCoupon');
        if (drawerCouponInput) drawerCouponInput.value = '';
        if (pageCouponInput) pageCouponInput.value = '';
        
        const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
        fetch(rootUrl + 'cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ discount: '' })
        })
          .then(res => res.json())
          .then(() => {
            showToast('Cupón eliminado');
            updateCouponUI();
            if (window.refreshCartPageContent) window.refreshCartPageContent();
            if (window.refreshCartDrawer) window.refreshCartDrawer(false);
          });
      });
    }
  });
}

// Global handler to clear coupon when clicking the banner "Quitar" button
document.addEventListener('click', (e) => {
  if (e.target && e.target.classList.contains('clear-coupon-btn')) {
    e.preventDefault();
    sessionStorage.removeItem('cart_discount_code');
    sessionStorage.removeItem('last_shown_coupon');
    const drawerCoupon = document.getElementById('CartCoupon');
    const pageCoupon = document.getElementById('CartPageCoupon');
    if (drawerCoupon) drawerCoupon.value = '';
    if (pageCoupon) pageCoupon.value = '';
    
    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    fetch(rootUrl + 'cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ discount: '' })
    })
      .then(res => res.json())
      .then(() => {
        updateCouponUI();
        showToast('Cupón eliminado');
        if (window.refreshCartPageContent) {
          window.refreshCartPageContent();
        } else if (window.refreshCartDrawer) {
          window.refreshCartDrawer(false);
        }
      });
  }
});

// Intercept checkout form submissions to append the discount code
document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!form) return;
  
  const actionAttr = form.getAttribute('action');
  if (typeof actionAttr === 'string') {
    // Only intercept checkout or cart summary forms. NEVER modify product add to cart, update, or change actions
    const isCartAddUpdateOrChange = actionAttr.includes('/cart/add') || actionAttr.includes('/cart/update') || actionAttr.includes('/cart/change');
    const isCheckoutOrCartPage = actionAttr.includes('/checkout') || actionAttr.includes('/cart');

    if (isCheckoutOrCartPage && !isCartAddUpdateOrChange) {
      const drawerCoupon = document.getElementById('CartCoupon');
      const pageCoupon = document.getElementById('CartPageCoupon');

      let discountVal = '';
      // Prefer active input values in the DOM, fallback to sessionStorage
      if (drawerCoupon) {
        discountVal = drawerCoupon.value.trim();
      } else if (pageCoupon) {
        discountVal = pageCoupon.value.trim();
      } else {
        discountVal = sessionStorage.getItem('cart_discount_code') || '';
      }

      if (discountVal) {
        sessionStorage.setItem('cart_discount_code', discountVal);
        try {
          const actionUrl = new URL(actionAttr, window.location.origin);
          actionUrl.searchParams.set('discount', discountVal);
          form.setAttribute('action', actionUrl.pathname + actionUrl.search);
        } catch (err) {
          console.error('Error modifying form action:', err);
        }
      } else {
        sessionStorage.removeItem('cart_discount_code');
        try {
          const actionUrl = new URL(actionAttr, window.location.origin);
          if (actionUrl.searchParams.has('discount')) {
            actionUrl.searchParams.delete('discount');
            form.setAttribute('action', actionUrl.pathname + actionUrl.search);
          }
        } catch (err) {
          console.error('Error modifying form action:', err);
        }
      }
    }
  }
});



// Expose functions to the global window scope so other scripts/components can use them
window.refreshCartDrawer = refreshCartDrawer;
window.updateCartBadges = updateCartBadges;
window.initCartAjaxHandlers = initCartAjaxHandlers;
window.initProductCards = initProductCards;
window.initCartCoupon = initCartCoupon;

/**
 * Initializes the main product page buy buttons (AJAX for Añadir al carrito)
 */
function initProductPageBuyButtons() {
  const form = document.querySelector('form[id^="product-form-"]') || document.getElementById('ProductForm');
  if (!form) return;

  const addToCartBtn = document.getElementById('ProductAddToCartBtn');
  const stickyForm = document.getElementById('sticky-product-form');
  const stickySubmitBtn = document.getElementById('StickySubmitBtn');

  // Helper function to submit product via AJAX and open the drawer
  const addProductViaAjax = (variantId, quantity, btn) => {
    if (!variantId) {
      console.error('No variant ID found for addition');
      return;
    }

    const isSticky = btn === stickySubmitBtn;
    const btnTextSpan = isSticky ? btn.querySelector('.sticky-btn-text') : btn.querySelector('.btn-text');
    if (!btnTextSpan) return;

    const originalContent = btnTextSpan.innerHTML;

    if (isSticky) {
      btnTextSpan.textContent = 'Añadiendo...';
    } else {
      btnTextSpan.textContent = 'Añadiendo...';
    }
    btn.disabled = true;

    const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/';
    fetch(`${rootUrl}cart/add.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: new URLSearchParams({
        'id': variantId,
        'quantity': quantity
      }).toString()
    })
      .then(res => {
        if (!res.ok) throw new Error('AJAX Product Page Add fail');
        return res.json();
      })
      .then(item => {
        btnTextSpan.textContent = '¡Añadido!';

        // Refresh and open global theme cart drawer, or refresh cart page if we're on the main cart page!
        if (window.refreshCartPageContent) {
          window.refreshCartPageContent();
        } else if (window.refreshCartDrawer) {
          window.refreshCartDrawer(true);
        }

        setTimeout(() => {
          if (isSticky) {
            // Re-trigger the variant sync update to restore original price/label
            const priceDisplay = document.getElementById('StickyProductPrice');
            const formattedPrice = priceDisplay ? priceDisplay.textContent : '';
            btnTextSpan.innerHTML = `Añadir al carrito - <span class="sticky-btn-price">${formattedPrice}</span>`;
          } else {
            btnTextSpan.textContent = 'Añadir al carrito';
          }
          btn.disabled = false;
        }, 1500);
      })
      .catch(err => {
        console.error(err);
        if (window.showToast) {
          window.showToast('Error al añadir al carrito. Por favor, inténtelo de nuevo.');
        } else {
          alert('Error al añadir al carrito. Por favor, inténtelo de nuevo.');
        }

        if (isSticky) {
          const priceDisplay = document.getElementById('StickyProductPrice');
          const formattedPrice = priceDisplay ? priceDisplay.textContent : '';
          btnTextSpan.innerHTML = `Añadir al carrito - <span class="sticky-btn-price">${formattedPrice}</span>`;
        } else {
          btnTextSpan.textContent = 'Añadir al carrito';
        }
        btn.disabled = false;
      });
  };

  // Helper to detect if any bundle app (like Fast Bundle) is active on the page
  const isBundleActive = () => {
    return window.FastBundle || 
           window.fastBundle || 
           window.rbrBundle || 
           window.rbrBundleBuilder || 
           window.RBR || 
           document.querySelector('.rbrBundleBuilder, [class*="fast-bundle"], [class*="fast_bundle"], [class*="rbr"], [class*="bundle-builder"], .fast_bundle_container');
  };

  // Helper to detect if the request payload or form content contains bundle parameters
  const isBundleRequest = (body) => {
    if (!body) return false;
    let bodyStr = "";
    if (body instanceof FormData) {
      for (let [key, val] of body.entries()) {
        bodyStr += key + "=" + val + "&";
      }
    } else if (body instanceof URLSearchParams) {
      bodyStr = body.toString();
    } else if (typeof body === 'string') {
      bodyStr = body;
    } else {
      try {
        bodyStr = JSON.stringify(body);
      } catch (e) {
        bodyStr = "";
      }
    }
    return bodyStr.includes('__bundle') || 
           bodyStr.includes('bundle_id') || 
           bodyStr.includes('fast-bundle-') || 
           bodyStr.includes('properties[_');
  };

  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', (e) => {
      // If Fast Bundle is active on the page AND the product form contains bundle parameters, bypass custom AJAX submission
      if (isBundleActive() && isBundleRequest(form ? new FormData(form) : null)) {
        return;
      }
      e.preventDefault();
      const variantIdInput = document.getElementById('SelectedVariantId');
      const qtyInput = document.getElementById('ProductQtyInput');

      const variantId = variantIdInput ? variantIdInput.value : '';
      const quantity = qtyInput ? qtyInput.value : '1';

      addProductViaAjax(variantId, quantity, addToCartBtn);
    });
  }

  if (stickySubmitBtn && stickyForm) {
    stickyForm.addEventListener('submit', (e) => {
      if (isBundleActive() && isBundleRequest(new FormData(stickyForm))) {
        return;
      }
      e.preventDefault();
    });
    stickySubmitBtn.addEventListener('click', (e) => {
      // If Fast Bundle is active on the page AND the sticky form contains bundle parameters, bypass custom AJAX submission
      if (isBundleActive() && isBundleRequest(new FormData(stickyForm))) {
        return;
      }
      e.preventDefault();
      const variantIdInput = document.getElementById('StickyVariantId');
      const qtyInput = document.getElementById('StickyQtyHidden');

      const variantId = variantIdInput ? variantIdInput.value : '';
      const quantity = qtyInput ? qtyInput.value : '1';

      addProductViaAjax(variantId, quantity, stickySubmitBtn);
    });
  }

}

/**
 * Global Wishlist Manager with LocalStorage Persistence
 */
const themeWishlist = {
  get() {
    try {
      return JSON.parse(localStorage.getItem('theme_wishlist')) || [];
    } catch (e) {
      return [];
    }
  },

  save(list) {
    localStorage.setItem('theme_wishlist', JSON.stringify(list));
    this.updateUI();
  },

  has(handle) {
    if (!handle) return false;
    const list = this.get();
    return list.some(item => item.handle === handle);
  },

  toggle(productData) {
    if (!productData || !productData.handle) return false;
    let list = this.get();
    const index = list.findIndex(item => item.handle === productData.handle);
    let added = false;

    if (index === -1) {
      list.push(productData);
      added = true;
      showToast('¡Agregado a la lista de deseos!');
    } else {
      list.splice(index, 1);
      showToast('¡Eliminado de la lista de deseos!');
    }

    this.save(list);
    return added;
  },

  remove(handle) {
    if (!handle) return;
    let list = this.get();
    list = list.filter(item => item.handle !== handle);
    this.save(list);
  },

  updateUI() {
    const list = this.get();
    const count = list.length;

    // Update count badges
    const badgeDesktop = document.getElementById('WishlistCount');
    const badgeMobile = document.getElementById('MobileWishlistCount');
    const badgeHamburger = document.getElementById('HamburgerWishlistCount');

    if (badgeDesktop) {
      badgeDesktop.textContent = count;
      badgeDesktop.style.display = count > 0 ? 'flex' : 'none';
    }
    if (badgeMobile) {
      badgeMobile.textContent = count;
      badgeMobile.style.display = count > 0 ? 'flex' : 'none';
    }
    if (badgeHamburger) {
      badgeHamburger.textContent = count;
      badgeHamburger.style.display = count > 0 ? 'inline-flex' : 'none';
    }

    // Update card buttons
    document.querySelectorAll('.product-card-item').forEach(card => {
      const handle = card.dataset.productHandle;
      if (!handle) return;

      const btn = card.querySelector('.wishlist-btn');
      if (!btn) return;

      const icon = btn.querySelector('svg');
      const isActive = this.has(handle);

      if (isActive) {
        btn.classList.add('is-active');
        btn.style.backgroundColor = '#e03a3a';
        btn.style.color = '#ffffff';
        if (icon) icon.setAttribute('fill', 'currentColor');
      } else {
        btn.classList.remove('is-active');
        btn.style.backgroundColor = '';
        btn.style.color = '';
        if (icon) icon.setAttribute('fill', 'none');
      }
    });

    // Update main product details page buttons
    const mainBtn = document.getElementById('MainProductWishlistBtn');
    if (mainBtn) {
      const handle = mainBtn.dataset.productHandle;
      const isActive = this.has(handle);
      const icon = mainBtn.querySelector('svg');

      if (isActive) {
        mainBtn.classList.add('is-active');
        if (icon) icon.setAttribute('fill', 'currentColor');
      } else {
        mainBtn.classList.remove('is-active');
        if (icon) icon.setAttribute('fill', 'none');
      }
    }

    // Render wishlist drawer list
    const drawerWrapper = document.querySelector('.wishlist-drawer-wrapper');
    const drawerList = document.getElementById('WishlistDrawerItems');
    if (drawerWrapper && drawerList) {
      if (count === 0) {
        drawerWrapper.classList.add('is-wishlist-empty');
        drawerList.innerHTML = '';
      } else {
        drawerWrapper.classList.remove('is-wishlist-empty');
        drawerList.innerHTML = list.map(item => `
          <div class="wishlist-drawer-item" data-product-handle="${item.handle}">
            <div class="wishlist-item-image-box">
              <a href="${item.url || '#'}">
                <img src="${item.image || '//cdn.shopify.com/s/images/themes/product-1.png'}" alt="${item.title}" class="wishlist-item-img" loading="lazy">
              </a>
            </div>
            <div class="wishlist-item-details-box">
              <h3 class="wishlist-item-name">
                <a href="${item.url || '#'}">${item.title}</a>
              </h3>
              <div class="wishlist-item-price-val">
                ${item.price}
                ${item.compare_price ? `<span class="wishlist-item-compare-val">${item.compare_price}</span>` : ''}
              </div>
              <div class="wishlist-item-actions">
                <button type="button" class="wishlist-item-add-to-cart" data-variant-id="${item.variant_id || ''}" data-product-handle="${item.handle}">
                  Añadir al carrito
                </button>
                <button type="button" class="wishlist-item-remove" data-product-handle="${item.handle}">
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        `).join('');

        // Rebind events for newly created drawer elements
        this.bindDrawerActions();
      }
    }
  },

  bindDrawerActions() {
    const drawerList = document.getElementById('WishlistDrawerItems');
    if (!drawerList) return;

    // 1. Remove item button
    drawerList.querySelectorAll('.wishlist-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const handle = btn.dataset.productHandle;
        this.remove(handle);
      });
    });

    // 2. Add to cart button
    drawerList.querySelectorAll('.wishlist-item-add-to-cart').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const variantId = btn.dataset.variantId;
        if (!variantId) return;

        // Show loading spinner/text
        const originalText = btn.textContent;
        btn.textContent = 'Agregando...';
        btn.disabled = true;

        // Add to Cart via dynamic AJAX API request
        const formData = {
          items: [{
            id: variantId,
            quantity: 1
          }]
        };

        fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root + 'cart/add.js' : '/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })
          .then(response => {
            if (!response.ok) {
              throw new Error('Error adding to cart');
            }
            return response.json();
          })
          .then(data => {
            btn.textContent = '¡Agregado!';
            showToast('¡Producto añadido al carrito!');

            // Refresh the global Cart Drawer if available
            if (window.refreshCartDrawer) {
              window.refreshCartDrawer(true);
            } else {
              window.location.href = '/cart';
            }

            // Close wishlist drawer
            const wishlistDrawer = document.getElementById('WishlistDrawer');
            if (wishlistDrawer) {
              wishlistDrawer.classList.remove('is-open');
            }

            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          })
          .catch(error => {
            console.error('Error adding to cart:', error);
            btn.textContent = 'Error';
            setTimeout(() => {
              btn.textContent = originalText;
              btn.disabled = false;
            }, 1500);
          });
      });
    });
  }
};
window.themeWishlist = themeWishlist;

/**
 * Initializes interactive behaviors for the Wishlist Drawer
 */
function initWishlistDrawer() {
  const wishlistDrawer = document.getElementById('WishlistDrawer');
  const wishlistClose = document.querySelector('.wishlist-drawer-close');
  const wishlistOverlay = document.querySelector('.wishlist-drawer-overlay');

  if (!wishlistDrawer) return;

  // Open Wishlist Drawer
  const triggers = document.querySelectorAll('#HeaderWishlistTrigger, #MobileWishlistTrigger, .hamburger-wishlist-trigger');
  triggers.forEach(trigger => {
    if (trigger.dataset.drawerInitialized) return;
    trigger.dataset.drawerInitialized = 'true';
    trigger.addEventListener('click', (e) => {
      e.preventDefault();

      // Close mobile navigation drawer if open
      const mobileNavDrawer = document.getElementById('MobileNavDrawer');
      const mobileDrawerOverlay = document.querySelector('.mobile-drawer-overlay');
      if (mobileNavDrawer) {
        mobileNavDrawer.classList.remove('is-open');
      }
      if (mobileDrawerOverlay) {
        mobileDrawerOverlay.classList.remove('is-active');
      }

      wishlistDrawer.classList.add('is-open');
      document.body.style.overflow = 'hidden'; // Lock background scrolling
    });
  });

  // Close Wishlist Drawer
  const closeDrawer = () => {
    wishlistDrawer.classList.remove('is-open');
    document.body.style.overflow = '';
  };

  if (wishlistClose) wishlistClose.addEventListener('click', closeDrawer);
  if (wishlistOverlay) wishlistOverlay.addEventListener('click', closeDrawer);
}

/**
 * Initializes interactive behaviors for the Main Product Details page Wishlist button
 */
function initMainProductWishlist() {
  const mainBtn = document.getElementById('MainProductWishlistBtn');
  if (!mainBtn) return;

  if (mainBtn.dataset.wishlistInitialized) return;
  mainBtn.dataset.wishlistInitialized = 'true';

  mainBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Find matching selected variant ID from form input if available
    const selectedVariantIdInput = document.getElementById('SelectedVariantId');
    const activeVariantId = selectedVariantIdInput ? selectedVariantIdInput.value : mainBtn.dataset.variantId;

    const productData = {
      handle: mainBtn.dataset.productHandle,
      title: mainBtn.dataset.productTitle,
      price: mainBtn.dataset.productPrice,
      compare_price: mainBtn.dataset.productComparePrice || null,
      image: mainBtn.dataset.productImage,
      url: mainBtn.dataset.productUrl,
      variant_id: activeVariantId
    };

    if (window.themeWishlist) {
      window.themeWishlist.toggle(productData);
    }
  });
}

/* ==========================================================================
   GLOBAL CART AJAX INTERCEPTOR FOR THIRD-PARTY APPS
   ========================================================================== */
(function () {
  // Helper to detect if any bundle app is active on the page
  const isBundleAppActive = () => {
    return window.FastBundle || 
           window.fastBundle || 
           window.rbrBundle || 
           window.rbrBundleBuilder || 
           window.RBR || 
           document.querySelector('.rbrBundleBuilder, [class*="fast-bundle"], [class*="fast_bundle"], [class*="rbr"], [class*="bundle-builder"], .fast_bundle_container');
  };

  const isBundleRequest = (body) => {
    if (!body) return false;
    let bodyStr = "";
    if (body instanceof FormData) {
      for (let [key, val] of body.entries()) {
        bodyStr += key + "=" + val + "&";
      }
    } else if (body instanceof URLSearchParams) {
      bodyStr = body.toString();
    } else if (typeof body === 'string') {
      bodyStr = body;
    } else {
      try {
        bodyStr = JSON.stringify(body);
      } catch (e) {
        bodyStr = "";
      }
    }
    return bodyStr.includes('__bundle') || 
           bodyStr.includes('bundle_id') || 
           bodyStr.includes('fast-bundle-') || 
           bodyStr.includes('properties[_');
  };

  const handleCartSync = (urlString) => {
    setTimeout(() => {
      if (window.location.pathname === '/cart' && window.refreshCartPageContent) {
        window.refreshCartPageContent();
      } else if (window.refreshCartDrawer) {
        const shouldOpen = urlString.includes('/cart/add');
        window.refreshCartDrawer(shouldOpen);
      }
    }, 250); // Small delay to let Shopify process database writes
  };

  // 1. Intercept Fetch API
  const originalFetch = window.fetch;
  window.fetch = function () {
    const [url, options] = arguments;
    
    // If Fast Bundle is active AND this is specifically a bundle request, bypass interception
    if (isBundleAppActive() && isBundleRequest(options && options.body)) {
      return originalFetch.apply(this, arguments);
    }

    const urlString = typeof url === 'string' ? url : (url instanceof URL ? url.href : (url && url.url ? url.url : ''));

    if (urlString.includes('/cart/add') || urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
      return originalFetch.apply(this, arguments).then(response => {
        if (response.ok) {
          handleCartSync(urlString);
        }
        return response;
      });
    }
    return originalFetch.apply(this, arguments);
  };

  // 2. Intercept XMLHttpRequest API (jQuery / Axios fallbacks)
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._url = typeof url === 'string' ? url : (url instanceof URL ? url.href : '');
    return originalOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    // If Fast Bundle is active AND this is specifically a bundle request, bypass interception
    if (isBundleAppActive() && isBundleRequest(body)) {
      return originalSend.apply(this, arguments);
    }

    this.addEventListener('load', function () {
      if (this.status >= 200 && this.status < 300) {
        const urlString = this._url || '';
        if (urlString.includes('/cart/add') || urlString.includes('/cart/change') || urlString.includes('/cart/update')) {
          handleCartSync(urlString);
        }
      }
    });
    return originalSend.apply(this, arguments);
  };
})();
